import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';

interface LazyLoaderProps {
  children: React.ReactNode;
  placeholder: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  once?: boolean;
  rootMargin?: string;
  threshold?: number | number[];
  onVisible?: () => void;
  onHidden?: () => void;
}

const LazyLoader = ({
  children,
  placeholder = null,
  className = '',
  style = {},
  onContextMenu,
  once = true,
  rootMargin = '50px',
  threshold = 0.01,
  disabled = false,
  debug = false,
  onVisible,
  onHidden,
  fallback = null,
  ssrVisible = false,
  id,
}) => {
  const [isVisible, setIsVisible] = useState(() => {
    if (disabled) return true;
    if (typeof window === 'undefined') return ssrVisible;
    return false;
  });
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef(null);
  const observerRef = useRef(null);
  const hasBeenVisible = useRef(false);

  // Memoize observer options to prevent unnecessary reconnections
  const observerOptions = useMemo(
    () => ({ rootMargin, threshold }),
    [rootMargin, threshold]
  );

  const log = useCallback(
    (message) => {
      if (debug) {
        console.log(`[LazyLoader${id ? ` #${id}` : ''}]: ${message}`);
      }
    },
    [debug, id]
  );

  const handleVisibilityChange = useCallback(
    (isIntersecting) => {
      if (isIntersecting) {
        if (!hasBeenVisible.current) {
          hasBeenVisible.current = true;
          log('First time visible');
        }

        setIsVisible(true);
        onVisible?.();

        if (once && observerRef.current && containerRef.current) {
          observerRef.current.unobserve(containerRef.current);
          log('Unobserved after first visibility (once mode)');
        }
      } else if (!once) {
        setIsVisible(false);
        onHidden?.();
        log('Hidden');
      }
    },
    [once, onVisible, onHidden, log]
  );

  useEffect(() => {
    if (disabled) {
      log('Disabled - showing content immediately');
      return;
    }

    const node = containerRef.current;
    if (!node) {
      log('No container ref available');
      return;
    }

    // Check if Intersection Observer is supported
    if (!('IntersectionObserver' in window)) {
      log('IntersectionObserver not supported - showing content');
      setIsVisible(true);
      return;
    }

    try {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          log(
            `Intersection ratio: ${entry.intersectionRatio}, isIntersecting: ${entry.isIntersecting}`
          );
          handleVisibilityChange(entry.isIntersecting);
        });
      }, observerOptions);

      observerRef.current = observer;
      observer.observe(node);
      log('Started observing');

      return () => {
        observer.disconnect();
        log('Disconnected observer');
      };
    } catch (error) {
      console.error('LazyLoader: Failed to create IntersectionObserver', error);
      setIsVisible(true);
    }
  }, [disabled, observerOptions, handleVisibilityChange, log]);

  useEffect(() => {
    if (hasError) {
      log('Error detected in children');
    }
  }, [hasError, log]);

  // Performance mark when content becomes visible
  useEffect(() => {
    if (isVisible && hasBeenVisible.current && 'performance' in window) {
      const markName = `lazy-load-visible${id ? `-${id}` : ''}-${Date.now()}`;
      try {
        performance.mark(markName);
      } catch (e) {
        // Ignore performance mark errors
      }
    }
  }, [isVisible, id]);

  // Handle errors in children
  const renderContent = () => {
    if (hasError) {
      return (
        fallback || (
          <div className="lazy-loader-error">Error loading content</div>
        )
      );
    }

    try {
      return isVisible ? children : placeholder;
    } catch (error) {
      setHasError(true);
      return (
        fallback || (
          <div className="lazy-loader-error">Error loading content</div>
        )
      );
    }
  };

  return (
    <div
      ref={containerRef}
      id={id}
      className={className}
      style={style}
      onContextMenu={onContextMenu}
      data-lazy-loader
      data-visible={isVisible}
    >
      {renderContent()}
    </div>
  );
};

export default LazyLoader;
